import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, TableCell, TableRow } from "@mui/material";
import Basket from "assets/img/svg/basket";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Fragment, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AccountingMapping } from "store/types";
import { AccountingAccountCategories } from "store/types";

import GectMappingFields from "./GectMappingFields";

/**
 * Number of columns of the mapping table
 */
const COLUMNS = 7;

interface IMappingLine {
    line: AccountingMapping;
    idx: number;
    valueTypeList: Array<{ id: string; label: string }>;
    handleMappingValueChange: <K extends keyof AccountingMapping>(key: K, index: number, value: any) => void;
    handleMappingDeletion: (line: AccountingMapping, appearanceOrder: number) => void;
}

const MappingLine = ({ line, idx, valueTypeList, handleMappingValueChange, handleMappingDeletion }: IMappingLine) => {
    const [t] = useTranslation();
    const { getCompanySettings, usesCegidGect } = useCompanySettings();
    const usesIRCEC = getCompanySettings("uses_ircec");

    const typeList = useMemo(() => {
        let list = [...valueTypeList];
        if (usesIRCEC) {
            list = [
                ...list,
                { id: "totalIrcecBeneficiary", label: t(`Accounting.Form.totalIrcecBeneficiary`) },
                { id: "totalIrcecPublisher", label: t(`Accounting.Form.totalIrcecPublisher`) },
            ];
        }
        return list;
    }, [t, valueTypeList, usesIRCEC]);

    return (
        <Fragment>
            <TableRow>
                <TableCell className="body-cell">
                    <StyledTextField
                        size="small"
                        placeholder={t("Accounting.JournalCode")}
                        value={line.journalCode}
                        onChange={(e) => handleMappingValueChange("journalCode", idx, e.target.value)}
                        className="entry-input"
                    />
                </TableCell>
                <TableCell className="body-cell">
                    <StyledTextField
                        size="small"
                        placeholder={t("Accounting.AccountNumber")}
                        value={line.accountNumber}
                        onChange={(e) => handleMappingValueChange("accountNumber", idx, e.target.value)}
                        className="entry-input"
                    />
                </TableCell>
                <TableCell className="body-cell">
                    <StyledSelect
                        size="small"
                        name="type"
                        className="entry-select type"
                        value={line.accountType}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{ className: "select-menu" }}
                        onChange={(e) => handleMappingValueChange("accountType", idx, e.target.value)}
                    >
                        {typeList.map(({ id, label }) => (
                            <StyledMenuItem key={`${id}-${line.id}`} value={id} className="select-menu-item">
                                {label}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </TableCell>
                <TableCell className="body-cell">
                    <StyledSelect
                        size="small"
                        name="direction"
                        className="entry-select"
                        value={line.direction}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{ className: "select-menu" }}
                        sx={{
                            color: line.direction === "debit" ? "rgba(39, 90, 176, 1)" : "rgba(155, 80, 103, 1)",
                        }}
                        onChange={(e) => handleMappingValueChange("direction", idx, e.target.value)}
                    >
                        <StyledMenuItem
                            className="select-menu-item"
                            value="debit"
                            sx={{
                                color: "rgba(39, 90, 176, 1)",
                                fontFamily: "inherit",
                            }}
                        >
                            {t("Accounting.Form.Debit")}
                        </StyledMenuItem>
                        <StyledMenuItem
                            className="select-menu-item"
                            value="credit"
                            sx={{
                                color: "rgba(155, 80, 103, 1)",
                                fontFamily: "inherit",
                            }}
                        >
                            {t("Accounting.Form.Credit")}
                        </StyledMenuItem>
                    </StyledSelect>
                </TableCell>
                <TableCell className="body-cell">
                    <StyledSelect
                        size="small"
                        name="accountCategory"
                        className="entry-select"
                        value={line.accountCategory}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{ className: "select-menu" }}
                        onChange={(e) => handleMappingValueChange("accountCategory", idx, e.target.value)}
                    >
                        {AccountingAccountCategories.map((type) => (
                            <StyledMenuItem key={`${type}-${line.id}`} value={type} className="select-menu-item">
                                {t(`Accounting.Form.${type}`)}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </TableCell>
                <TableCell className="body-cell">
                    <StyledSelect
                        size="small"
                        name="attachAuxiliaryOnExport"
                        className="entry-select attachAuxiliary"
                        value={line.attachAuxiliaryOnExport}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{ className: "select-menu" }}
                        onChange={(e) => handleMappingValueChange("attachAuxiliaryOnExport", idx, e.target.value)}
                    >
                        <StyledMenuItem value={0} className="select-menu-item">
                            {t("Accounting.ExportModal.No")}
                        </StyledMenuItem>
                        <StyledMenuItem value={1} className="select-menu-item">
                            {t("Accounting.ExportModal.Yes")}
                        </StyledMenuItem>
                    </StyledSelect>
                </TableCell>
                <TableCell className="body-cell">
                    <Button
                        size="small"
                        className="delete-btn"
                        variant="outlined"
                        onClick={() => {
                            handleMappingDeletion(line, line.appearanceOrder);
                        }}
                    >
                        <Basket color="#000000" />
                    </Button>
                </TableCell>
            </TableRow>
            {usesCegidGect && (
                <TableRow>
                    <TableCell className="body-cell gect-cell" colSpan={COLUMNS}>
                        <GectMappingFields
                            gect={line.gect ?? {}}
                            onChange={(gect) => handleMappingValueChange("gect", idx, gect)}
                        />
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
};

export default memo(MappingLine);
